import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { handlePlanLimit } from "@/lib/planLimit";
import DashboardLayout from "@/components/DashboardLayout";
import { Lightning, Plus, UploadSimple, Trash, Users, Funnel, Star } from "@phosphor-icons/react";

const QUALITY_TAG = { good: "tag tag-good", bad: "tag tag-bad", risky: "tag tag-risky" };

export default function LeadsModule() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [uploadStat, setUploadStat] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", notes: "", source: "manual" });
  const fileRef = useRef(null);

  const refresh = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };
  useEffect(() => { refresh(); }, []);

  const processAll = async () => {
    setBatchBusy(true);
    try {
      const { data } = await api.post("/leads/process-all");
      if (data?.skipped_quota > 0) {
        toast.warning(`Scored ${data.processed} leads. ${data.skipped_quota} were skipped — your plan limit was reached.`, {
          action: { label: "Upgrade", onClick: () => { window.location.href = "/pricing"; } },
          duration: 9000,
        });
      } else if ((data?.processed || 0) > 0) {
        toast.success(`Scored ${data.processed} lead${data.processed > 1 ? "s" : ""}.`);
      }
      await refresh();
    } catch (err) {
      if (!handlePlanLimit(err)) toast.error(err.response?.data?.detail || "Batch failed.");
    } finally { setBatchBusy(false); }
  };
  const scoreOne = async (id) => {
    setBusy(true);
    try {
      await api.post(`/leads/${id}/process`);
      await refresh();
    } catch (err) {
      if (!handlePlanLimit(err)) toast.error(err.response?.data?.detail || "Could not score lead.");
    } finally { setBusy(false); }
  };
  const onAdd = async (e) => {
    e.preventDefault();
    await api.post("/leads", form);
    setShowAdd(false);
    setForm({ name: "", email: "", phone: "", company: "", notes: "", source: "manual" });
    await refresh();
  };
  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    const { data } = await api.post("/leads/upload-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setUploadStat(data);
    await refresh();
    e.target.value = "";
  };
  const onDelete = async (id) => {
    await api.delete(`/leads/${id}`);
    await refresh();
  };

  const filtered = leads.filter((l) => filter === "all" ? true : (filter === "duplicates" ? l.duplicate : l.quality === filter));
  const stats = {
    total: leads.length,
    good: leads.filter(l => l.quality === "good").length,
    risky: leads.filter(l => l.quality === "risky").length,
    bad: leads.filter(l => l.quality === "bad").length,
    duplicates: leads.filter(l => l.duplicate).length,
  };

  return (
    <DashboardLayout
      title="Lead Processing"
      actions={
        <>
          <input ref={fileRef} type="file" accept=".csv" onChange={onUpload} className="hidden" data-testid="csv-upload-input" />
          <button onClick={() => fileRef.current?.click()} className="btn-secondary text-sm" data-testid="upload-csv-btn">
            <UploadSimple size={16} weight="bold" /> Upload CSV
          </button>
          <button onClick={() => setShowAdd(true)} className="btn-secondary text-sm" data-testid="add-lead-btn">
            <Plus size={16} weight="bold" /> Add lead
          </button>
          <button onClick={processAll} disabled={batchBusy} className="btn-primary text-sm" data-testid="score-all-btn">
            <Lightning size={16} weight="fill" /> {batchBusy ? "Scoring…" : "Score all"}
          </button>
        </>
      }
    >
      {uploadStat && (
        <div className="mb-4 cell-strong p-4 bg-[var(--accent)] text-sm" data-testid="upload-stat">
          Imported <strong>{uploadStat.inserted}</strong> leads · {uploadStat.duplicates} flagged as duplicates.
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        <Stat label="Total" value={stats.total} />
        <Stat label="Good" value={stats.good} className="bg-green-50" />
        <Stat label="Risky" value={stats.risky} className="bg-yellow-50" />
        <Stat label="Bad" value={stats.bad} className="bg-red-50" />
        <Stat label="Duplicates" value={stats.duplicates} />

        <div className="col-span-12 cell-strong bg-white">
          <div className="px-4 py-3 border-b border-[var(--line)] flex items-center gap-2 flex-wrap">
            <Funnel size={18} weight="duotone" className="text-[var(--ink-soft)]" />
            {["all", "good", "risky", "bad", "duplicates"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-wide font-mono-rc border ${filter === f ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)] hover:border-[var(--ink)]"}`}
              >{f}</button>
            ))}
            <span className="ml-auto text-xs text-[var(--ink-soft)] font-mono-rc">{filtered.length} shown</span>
          </div>

          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg-soft)] border-b border-[var(--line)]">
                <tr className="text-left">
                  <th className="px-4 py-3 overline text-[var(--ink-soft)]">Lead</th>
                  <th className="px-4 py-3 overline text-[var(--ink-soft)]">Contact</th>
                  <th className="px-4 py-3 overline text-[var(--ink-soft)]">Quality</th>
                  <th className="px-4 py-3 overline text-[var(--ink-soft)]">Score</th>
                  <th className="px-4 py-3 overline text-[var(--ink-soft)]">Route</th>
                  <th className="px-4 py-3 overline text-[var(--ink-soft)]">AI insight</th>
                  <th className="px-4 py-3 overline text-[var(--ink-soft)] text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.lead_id} className="border-b border-[var(--line)] hover:bg-[var(--bg-soft)]" data-testid={`lead-row-${l.lead_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{l.name}</div>
                      <div className="text-xs text-[var(--ink-soft)]">{l.company || "—"}</div>
                      {l.duplicate && <span className="tag tag-risky mt-1">DUPLICATE</span>}
                    </td>
                    <td className="px-4 py-3 font-mono-rc text-xs">
                      <div>{l.email || "—"}</div>
                      <div>{l.phone || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      {l.quality ? <span className={QUALITY_TAG[l.quality]}>{l.quality}</span> : <span className="text-xs text-[var(--ink-soft)]">unscored</span>}
                    </td>
                    <td className="px-4 py-3 font-display text-lg">{l.score != null ? l.score : "—"}</td>
                    <td className="px-4 py-3 font-mono-rc text-xs uppercase">{l.route || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[var(--ink-soft)] max-w-xs">{l.reason || l.notes || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {!l.processed && (
                          <button onClick={() => scoreOne(l.lead_id)} disabled={busy} className="px-2 py-1 text-xs border border-[var(--ink)] hover:bg-[var(--ink)] hover:text-white" data-testid={`score-${l.lead_id}`}>
                            Score
                          </button>
                        )}
                        <button onClick={() => onDelete(l.lead_id)} className="px-2 py-1 text-xs border border-[var(--bad)] text-[var(--bad)] hover:bg-[var(--bad)] hover:text-white" data-testid={`delete-${l.lead_id}`}>
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-12 text-[var(--ink-soft)]"><Users size={36} weight="duotone" className="mx-auto mb-2" /> No leads in this view.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowAdd(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={onAdd} className="cell-strong bg-white p-6 w-full max-w-lg" data-testid="add-lead-modal">
            <div className="font-display text-2xl mb-4">New lead</div>
            <div className="grid grid-cols-2 gap-3">
              <input className="input-rc col-span-2" required placeholder="Full name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="lead-name-input" />
              <input className="input-rc" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="lead-email-input" />
              <input className="input-rc" placeholder="Phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="lead-phone-input" />
              <input className="input-rc col-span-2" placeholder="Company" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} data-testid="lead-company-input" />
              <textarea className="input-rc col-span-2 h-24" placeholder="Notes / context" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} data-testid="lead-notes-input" />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary text-sm">Cancel</button>
              <button type="submit" className="btn-primary text-sm" data-testid="submit-lead-btn">Add lead</button>
            </div>
          </form>
        </div>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value, className = "" }) {
  return (
    <div className={`col-span-6 md:col-span-2 cell-strong p-4 ${className}`}>
      <div className="overline text-[var(--ink-soft)]">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}
